/**
 * useAnimationLoop - Manages RequestAnimationFrame loop for animation playback.
 * Extracted from useAnimationEngine and useAnimationInstance to eliminate duplication.
 */

import React, { useEffect, useRef } from 'react'
import type { AnimationAction, AnimationState } from '../types/Animation.types'

export type UseAnimationLoopOptions = {
	/** Ref containing current animation state (for reading in RAF callback) */
	stateRef: React.RefObject<AnimationState>
	/** Dispatch function for state updates */
	dispatch: React.Dispatch<AnimationAction>
	/** Callback when animation completes (only fires if not looping) */
	onComplete?: () => void
	/** Whether the loop should be active */
	isActive: boolean
}

/**
 * Hook that manages the animation loop using RequestAnimationFrame.
 * Updates animation state at 60fps when active.
 *
 * Uses the ref pattern to access current state in RAF callback without recreating
 * the loop on every state change. This is critical for performance.
 *
 * @param stateRef - Ref containing current animation state
 * @param dispatch - Dispatch function for animation actions
 * @param onComplete - Optional callback when animation completes (non-looping only)
 * @param isActive - Whether animation loop should run
 *
 * @example
 * // In useAnimationEngine (context-based)
 * const stateRef = useRef(state)
 * useEffect(() => { stateRef.current = state }, [state])
 *
 * const isActive = state.isPlaying && state.phase === 'execution'
 * useAnimationLoop({ stateRef, dispatch, onComplete, isActive })
 *
 * @example
 * // In useAnimationInstance (instance-based)
 * const stateRef = useRef(state)
 * useEffect(() => { stateRef.current = state }, [state])
 *
 * const isActive = state.isPlaying && state.phase === 'execution'
 * useAnimationLoop({
 *   stateRef,
 *   dispatch,
 *   onComplete: onCompleteRef.current,
 *   isActive,
 * })
 */
export function useAnimationLoop({
									 stateRef,
									 dispatch,
									 onComplete,
									 isActive,
								 }: UseAnimationLoopOptions): void {
	const rafIdRef = useRef<number | null>(null)
	const lastTimeRef = useRef<number>(0)
	const onCompleteRef = useRef(onComplete)

	// Keep onComplete callback ref up to date
	useEffect(() => {
		onCompleteRef.current = onComplete
	}, [onComplete])

	useEffect(() => {
		// Only run when active
		if (!isActive) {
			if (rafIdRef.current !== null) {
				cancelAnimationFrame(rafIdRef.current)
				rafIdRef.current = null
			}
			lastTimeRef.current = 0
			return
		}

		function animate(timestamp: number) {
			// Initialize lastTime on first frame
			if (lastTimeRef.current === 0) {
				lastTimeRef.current = timestamp
			}

			const currentState = stateRef.current
			if (!currentState) {
				return
			}

			// Calculate delta time with playback speed applied
			const rawDelta = timestamp - lastTimeRef.current
			const deltaTime = rawDelta * currentState.playbackSpeed
			lastTimeRef.current = timestamp

			// Dispatch tick to update state
			dispatch({ type: 'TICK', deltaTime })

			// Check if we should continue
			const newTime = currentState.currentTime + deltaTime

			if (newTime < currentState.totalDuration) {
				// Continue animation
				rafIdRef.current = requestAnimationFrame(animate)
			} else {
				// Animation complete
				if (currentState.loopMode) {
					// Reset and continue
					dispatch({ type: 'RESET' })
					dispatch({ type: 'PLAY' })
					rafIdRef.current = requestAnimationFrame(animate)
				} else {
					// Stop
					rafIdRef.current = null
					onCompleteRef.current?.()
				}
			}
		}

		// Start the animation loop
		rafIdRef.current = requestAnimationFrame(animate)

		// Cleanup on unmount or when inactive
		return () => {
			if (rafIdRef.current !== null) {
				cancelAnimationFrame(rafIdRef.current)
				rafIdRef.current = null
			}
			lastTimeRef.current = 0
		}
	}, [isActive, stateRef, dispatch])
	// Note: currentState properties (playbackSpeed, loopMode, totalDuration, currentTime)
	// are accessed via stateRef.current inside RAF callback, not added as dependencies
	// to avoid recreating the animation loop on every state change
}
